import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getInventoryLedger } from "../../services/inventoryLedgerService";

export default function InventoryLedger() {

  const [ledger,  setLedger]  = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchLedger(); }, []);

  const fetchLedger = async () => {
    try {
      const user      = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user.company_id || "";
      if (!companyId) { alert("Company ID not found. Please log in again."); return; }
      const data = await getInventoryLedger(companyId);
      setLedger(data);
    } catch (error) {
      console.error("Ledger fetch failed:", error);
      alert("Failed to load inventory ledger");
    } finally {
      setLoading(false);
    }
  };

  // ── RUNNING BALANCE — computed outside JSX ──────────────────────────
  // Sort oldest→newest per item, accumulate stock_effect, store on entry
  const ledgerWithBalance = (() => {
    // Step 1 — sort oldest first
    const sorted = [...ledger].sort((a, b) =>
      (a.created_at?.seconds ?? 0) - (b.created_at?.seconds ?? 0)
    );
    // Step 2 — accumulate per item_id
    const balanceMap = {};
    sorted.forEach(entry => {
      const key = entry.item_id || entry.item_code || entry.item_name || "";
      if (balanceMap[key] === undefined) balanceMap[key] = 0;
      balanceMap[key] += Number(entry.stock_effect || 0);
      entry._balance = balanceMap[key];
    });
    // Step 3 — re-sort newest first for display
    return sorted.sort((a, b) =>
      (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0)
    );
  })();

  if (loading) return (
    <div className="p-6 text-lg font-medium">Loading Inventory Ledger...</div>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-lg border p-6">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Inventory Ledger</h2>
          <div className="text-sm text-gray-500">
            Total Entries:{" "}
            <span className="font-semibold text-black">{ledger.length}</span>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full border-collapse min-w-[1400px]">

            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Item</th>
                <th className="p-3 text-left">Item Code</th>
                <th className="p-3 text-left">Movement</th>
                <th className="p-3 text-left">Qty</th>
                <th className="p-3 text-left">Effect</th>
                <th className="p-3 text-left">Balance</th>
                <th className="p-3 text-left">Unit</th>
                <th className="p-3 text-left">Site</th>
                <th className="p-3 text-left">Project</th>
                <th className="p-3 text-left">Reference</th>
                <th className="p-3 text-left">Remarks</th>
              </tr>
            </thead>

            <tbody>
              {ledgerWithBalance.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center p-8 text-gray-500">
                    No inventory records found
                  </td>
                </tr>
              ) : (
                ledgerWithBalance.map((entry, index) => (
                  <tr key={index}
                    className={`border-b transition-all hover:bg-slate-100 ${
                      index % 2 === 0 ? "bg-slate-50" : "bg-white"
                    }`}>

                    {/* DATE */}
                    <td className="p-3 whitespace-nowrap">
                      {entry.created_at?.seconds
                        ? new Date(entry.created_at.seconds * 1000).toLocaleString()
                        : "-"}
                    </td>

                    {/* ITEM */}
                    <td className="p-3 font-semibold whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/item-ledger/${encodeURIComponent(entry.item_code)}`)}
                        className="text-cyan-700 hover:text-cyan-900 hover:underline">
                        {entry.item_name || "-"}
                      </button>
                    </td>

                    {/* ITEM CODE */}
                    <td className="p-3 whitespace-nowrap">{entry.item_code || "-"}</td>

                    {/* MOVEMENT TYPE */}
                    <td className="p-3 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        entry.stock_effect > 0
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {entry.movement_type || "-"}
                      </span>
                    </td>

                    {/* QTY */}
                    <td className="p-3 font-medium">{entry.qty || 0}</td>

                    {/* STOCK EFFECT */}
                    <td className={`p-3 font-bold ${
                      entry.stock_effect > 0 ? "text-green-600" : "text-red-600"
                    }`}>
                      {entry.stock_effect > 0 ? "+" : ""}
                      {entry.stock_effect || 0}
                    </td>

                    {/* RUNNING BALANCE */}
                    <td className="p-3 font-semibold text-slate-800">
                      {Number(entry._balance ?? 0)}
                    </td>

                    {/* UNIT */}
                    <td className="p-3">{entry.unit || "-"}</td>

                    {/* SITE */}
                    <td className="p-3 whitespace-nowrap">
                      {entry.site_name || entry.site_id || "-"}
                    </td>

                    {/* PROJECT */}
                    <td className="p-3 whitespace-nowrap">
                      {entry.project_name || entry.project_id || "-"}
                    </td>

                    {/* REFERENCE */}
                    <td className="p-3 whitespace-nowrap text-sm">
                      <div className="font-medium">{entry.reference_type || "-"}</div>
                      <div className="text-gray-500 text-xs">{entry.reference_id || "-"}</div>
                    </td>

                    {/* REMARKS */}
                    <td className="p-3 min-w-[250px]">{entry.remarks || "-"}</td>

                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}